import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Paper,
  MenuItem,
} from '@mui/material'
import { useFormik } from 'formik'
import * as yup from 'yup'
import Layout from '../components/Layout'
import { supabase } from '../config/supabase'

const validationSchema = yup.object({
  first_name: yup.string().required('First name is required'),
  last_name: yup.string().required('Last name is required'),
  gender: yup.string().required('Gender is required'),
  date_of_birth: yup.date().required('Date of birth is required'),
  age_group: yup.string().required('Age group is required'),
  email: yup
    .string()
    .email('Enter a valid email')
    .nullable(),
  phone: yup
    .string()
    .matches(
      /^[0-9]{1,20}$/,
      'Phone number must be between 1 and 20 digits'
    )
    .required('Phone number is required'),
  address: yup.string().required('Address is required'),
  family_id: yup.string().uuid('Invalid family ID').nullable(),
  membership_date: yup.date().nullable(),
  baptism_date: yup.date().nullable(),
  membership_status: yup.string().required('Membership status is required'),
  marital_status: yup.string().required('Marital status is required'),
  hometown: yup.string().nullable(),
  previous_membership: yup.string().nullable(),
  notes: yup.string().nullable(), // Add notes field
})

const genderOptions = ['Male', 'Female', 'Other']
const ageGroupOptions = ['0-12', '13-18', '19-25', '26-35', '36-50', '51+']
const membershipStatusOptions = ['Active', 'Inactive', 'Visitor', 'Transferred']
const maritalStatusOptions = ['Single', 'Married', 'Widowed', 'Divorced']

function ContactForm() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [loading, setLoading] = useState(false)
  const [families, setFamilies] = useState([])

  const formik = useFormik({
    initialValues: {
      first_name: '',
      last_name: '',
      gender: '',
      date_of_birth: '',
      age_group: '',
      email: '',
      phone: '',
      address: '',
      family_id: '',
      membership_date: '',
      baptism_date: '',
      membership_status: '',
      marital_status: '',
      hometown: '',
      previous_membership: '',
      notes: '', // Add notes field
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true)
        const submissionValues = {
          ...values,
          family_id: values.family_id || null,
          date_of_birth: values.date_of_birth ? new Date(values.date_of_birth).toISOString() : null,
          membership_date: values.membership_date ? new Date(values.membership_date).toISOString() : null,
          baptism_date: values.baptism_date ? new Date(values.baptism_date).toISOString() : null,
        }
        
        if (id) {
          const { error } = await supabase
            .from('contacts')
            .update(submissionValues)
            .eq('id', id)
          if (error) throw error
        } else {
          const { error } = await supabase
            .from('contacts')
            .insert([submissionValues])
          if (error) throw error
        }
        navigate('/contacts')
      } catch (error) {
        console.error('Error saving contact:', error)
        alert('Error saving contact. Please try again.')
      } finally {
        setLoading(false)
      }
    },
  })

  useEffect(() => {
    fetchFamilies()
    if (id) {
      fetchContact()
    }
  }, [id])

  const fetchFamilies = async () => {
    try {
      const { data, error } = await supabase
        .from('families')
        .select('*')
        .order('family_name')
      
      if (error) throw error
      setFamilies(data)
    } catch (error) {
      console.error('Error fetching families:', error)
      alert('Error fetching families. Please try again.')
    }
  }

  const fetchContact = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error
      if (data) {
        formik.setValues(data)
      }
    } catch (error) {
      console.error('Error fetching contact:', error)
      navigate('/contacts')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Layout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {id ? 'Edit Contact' : 'Add New Contact'}
        </Typography>

        <Paper sx={{ p: 3 }}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="first_name"
                  name="first_name"
                  label="First Name"
                  value={formik.values.first_name}
                  onChange={formik.handleChange}
                  error={formik.touched.first_name && Boolean(formik.errors.first_name)}
                  helperText={formik.touched.first_name && formik.errors.first_name}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="last_name"
                  name="last_name"
                  label="Last Name"
                  value={formik.values.last_name}
                  onChange={formik.handleChange}
                  error={formik.touched.last_name && Boolean(formik.errors.last_name)}
                  helperText={formik.touched.last_name && formik.errors.last_name}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="gender"
                  name="gender"
                  select
                  label="Gender"
                  value={formik.values.gender}
                  onChange={formik.handleChange}
                  error={formik.touched.gender && Boolean(formik.errors.gender)}
                  helperText={formik.touched.gender && formik.errors.gender}
                >
                  {genderOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="date_of_birth"
                  name="date_of_birth"
                  label="Date of Birth"
                  type="date"
                  value={formik.values.date_of_birth}
                  onChange={formik.handleChange}
                  error={formik.touched.date_of_birth && Boolean(formik.errors.date_of_birth)}
                  helperText={formik.touched.date_of_birth && formik.errors.date_of_birth}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="age_group"
                  name="age_group"
                  select
                  label="Age Group"
                  value={formik.values.age_group}
                  onChange={formik.handleChange}
                  error={formik.touched.age_group && Boolean(formik.errors.age_group)}
                  helperText={formik.touched.age_group && formik.errors.age_group}
                >
                  {ageGroupOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label="Email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="phone"
                  name="phone"
                  label="Phone"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="address"
                  name="address"
                  label="Address"
                  multiline
                  rows={2}
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  error={formik.touched.address && Boolean(formik.errors.address)}
                  helperText={formik.touched.address && formik.errors.address}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="family_id"
                  name="family_id"
                  select
                  label="Family"
                  value={formik.values.family_id}
                  onChange={formik.handleChange}
                  error={formik.touched.family_id && Boolean(formik.errors.family_id)}
                  helperText={formik.touched.family_id && formik.errors.family_id}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {families.map((family) => (
                    <MenuItem key={family.id} value={family.id}>
                      {family.family_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="membership_date"
                  name="membership_date"
                  label="Membership Date"
                  type="date"
                  value={formik.values.membership_date}
                  onChange={formik.handleChange}
                  error={formik.touched.membership_date && Boolean(formik.errors.membership_date)}
                  helperText={formik.touched.membership_date && formik.errors.membership_date}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="baptism_date"
                  name="baptism_date"
                  label="Baptism Date"
                  type="date"
                  value={formik.values.baptism_date}
                  onChange={formik.handleChange}
                  error={formik.touched.baptism_date && Boolean(formik.errors.baptism_date)}
                  helperText={formik.touched.baptism_date && formik.errors.baptism_date}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="membership_status"
                  name="membership_status"
                  select
                  label="Membership Status"
                  value={formik.values.membership_status}
                  onChange={formik.handleChange}
                  error={formik.touched.membership_status && Boolean(formik.errors.membership_status)}
                  helperText={formik.touched.membership_status && formik.errors.membership_status}
                >
                  {membershipStatusOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="marital_status"
                  name="marital_status"
                  select
                  label="Marital Status"
                  value={formik.values.marital_status}
                  onChange={formik.handleChange}
                  error={formik.touched.marital_status && Boolean(formik.errors.marital_status)}
                  helperText={formik.touched.marital_status && formik.errors.marital_status}
                >
                  {maritalStatusOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="hometown"
                  name="hometown"
                  label="Hometown"
                  value={formik.values.hometown}
                  onChange={formik.handleChange}
                  error={formik.touched.hometown && Boolean(formik.errors.hometown)}
                  helperText={formik.touched.hometown && formik.errors.hometown}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="previous_membership"
                  name="previous_membership"
                  label="Previous Membership"
                  value={formik.values.previous_membership}
                  onChange={formik.handleChange}
                  error={formik.touched.previous_membership && Boolean(formik.errors.previous_membership)}
                  helperText={formik.touched.previous_membership && formik.errors.previous_membership}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="notes"
                  name="notes"
                  label="Notes"
                  value={formik.values.notes}
                  onChange={formik.handleChange}
                  multiline
                  rows={4}
                  helperText="Add any additional notes about the contact"
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => navigate('/contacts')}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Contact'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </Layout>
  )
}

export default ContactForm
